import { useRef } from 'react'

import MessagingArt from '@/components/sections/home/MessagingArt'
import Anchor from '@/components/ui/Anchor'
import Container from '@/components/ui/Container'
import Eyebrow from '@/components/ui/Eyebrow'
import { useData } from '@/hooks/useLocale'
import { useScrollProgress } from '@/hooks/useScrollProgress'
import { ramp, smooth } from '@/lib/scroll'

// 0 as the section's top enters the bottom of the screen; 1 once most of it is in.
// This section eases in and out (`smooth`) rather than the usual ease-out.
const progress = (el) => {
  const rect = el.getBoundingClientRect()
  return (
    (window.innerHeight - rect.top) /
    (window.innerHeight * 0.85 + rect.height * 0.25)
  )
}

/** Fades up from 18px below as progress `p` runs from `a` to `b`. */
const rise = (p, a, b) => {
  const t = smooth(ramp(p, a, b))
  return { opacity: t, transform: `translateY(${(1 - t) * 18}px)` }
}

/**
 * Internal messaging (design: Home, Messaging). As it scrolls in, the text
 * fades up line by line, the end of the headline fills with blue, and the
 * app screen beside it turns to face you.
 */
export default function MessagingSection() {
  const { MESSAGING } = useData()
  const section = useRef(null)
  const p = useScrollProgress(section, progress)
  const mark = smooth(ramp(p, 0.22, 0.55))

  return (
    <Container
      as="section"
      ref={section}
      id="messaging"
      className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))] items-center gap-14 overflow-x-clip py-[clamp(64px,9vw,110px)]"
    >
      <div className="flex flex-col gap-[18px]">
        <Eyebrow className="text-subtle" style={rise(p, 0, 0.15)}>
          {MESSAGING.eyebrow}
        </Eyebrow>
        <h2
          className="text-[clamp(30px,3.2vw,42px)] leading-[1.18] tracking-[-0.025em]"
          style={rise(p, 0.04, 0.2)}
        >
          {MESSAGING.title}{' '}
          <span
            className={`-mx-[.06em] rounded-xl bg-[linear-gradient(#e8f0fd,#e8f0fd)] box-decoration-clone bg-[position:0_50%] bg-no-repeat px-[.14em] transition-colors duration-350 rtl:bg-[position:100%_50%] ${mark > 0.5 ? 'text-brand' : ''}`}
            style={{ backgroundSize: `${mark * 100}% 100%` }}
          >
            {MESSAGING.mark}
          </span>
        </h2>
        <p
          className="max-w-115 text-[15px]/[1.7] text-muted"
          style={rise(p, 0.3, 0.5)}
        >
          {MESSAGING.text}
        </p>
        <Anchor
          href={MESSAGING.cta.href}
          className="mt-1.5 flex h-[46px] items-center self-start rounded-full bg-brand px-[22px] text-sm font-semibold text-white hover:bg-brand-dark"
          style={rise(p, 0.4, 0.6)}
        >
          {MESSAGING.cta.label}
        </Anchor>
      </div>
      <div className="w-full max-w-160 justify-self-center">
        <MessagingArt art={MESSAGING.art} t={smooth(smooth(ramp(p, 0.05, 0.9)))} />
      </div>
    </Container>
  )
}
